/**
 * The AI-generated consultation summary (frontend §12).
 *
 * Patient-facing language, built only from what the doctor approved. The
 * `generatedBy` flag is surfaced rather than hidden: a summary assembled from
 * the approved fields because the model was unavailable is still a good
 * summary, and the doctor should know which one they are reading before they
 * let it go to the patient.
 */

import { Sparkles, UserRound } from "lucide-react";

import type { ConsultationSummary } from "@/types/copilot";

const SECTIONS: Array<{ key: keyof ConsultationSummary; label: string }> = [
  { key: "discussed", label: "What was discussed" },
  { key: "diagnosisExplanation", label: "Diagnosis" },
  { key: "testsExplanation", label: "Tests" },
  { key: "treatmentInstructions", label: "Treatment" },
  { key: "lifestyleAdvice", label: "Lifestyle advice" },
  { key: "followUp", label: "Follow-up" },
];

export function ConsultationSummaryCard({ summary }: { summary: ConsultationSummary }) {
  const filled = SECTIONS.filter((section) => {
    const value = summary[section.key];
    return typeof value === "string" && value.trim().length > 0;
  });

  return (
    <section className="card-surface overflow-hidden" aria-label="Consultation summary">
      <header className="flex items-start gap-3 border-b border-border px-5 py-4">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <UserRound className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-base font-semibold">What the patient will see</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Written from the prescription you approved — nothing else.
          </p>
        </div>
        {summary.generatedBy === "ai" ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-info/25 bg-info/10 px-2 py-0.5 text-[0.65rem] font-semibold text-info">
            <Sparkles className="size-3" aria-hidden />
            AI written
          </span>
        ) : (
          <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[0.65rem] font-semibold text-muted-foreground">
            Plain restatement
          </span>
        )}
      </header>

      <div className="space-y-3.5 px-5 py-4">
        {summary.generatedBy === "fallback" && (
          <p className="rounded-lg bg-muted px-3 py-2 text-[0.7rem] leading-relaxed text-muted-foreground">
            The AI was unavailable, so this summary simply restates what you approved. It is
            accurate — just plainer than usual.
          </p>
        )}

        {filled.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            There was nothing recorded to summarise for this consultation.
          </p>
        ) : (
          filled.map((section) => (
            <div key={section.key}>
              <h3 className="text-[0.65rem] font-semibold tracking-wide text-muted-foreground uppercase">
                {section.label}
              </h3>
              <p className="mt-1 text-sm leading-relaxed whitespace-pre-line">
                {String(summary[section.key])}
              </p>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
